
import React, { useState, useEffect } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { Guide } from "@/types/guide";

const Guides = () => {
  const [guides, setGuides] = useState<Guide[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const { toast } = useToast();
  const { t, language } = useLanguage();

  useEffect(() => {
    const fetchGuides = async () => {
      try {
        console.log('=== Guides.tsx 開始取得指南資料 ===');
        
        const { data, error } = await supabase
          .from('guides')
          .select('*')
          .order('created_at', { ascending: false });
          
        if (error) {
          console.error('取得指南時發生錯誤:', error);
          throw error;
        }
        
        console.log('從資料庫取得的指南數量:', data?.length || 0);
        console.log('指南資料:', data);
        
        if (data) {
          // Process guides based on language
          const processedGuides = data.map(guide => {
            if (language === 'en' && guide.title_en) {
              return {
                ...guide,
                title: guide.title_en,
                summary: guide.summary_en,
                content: guide.content_en,
                tags: guide.tags_en || guide.tags // Use English tags if available
              };
            }
            return guide;
          });
          
          setGuides(processedGuides as Guide[]);
        }
      } catch (error: any) {
        console.error("Error fetching guides:", error);
        toast({
          variant: "destructive",
          title: t('errorFetchingGuides') || "無法載入租房攻略",
          description: error.message,
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchGuides();
  }, [toast, t, language]);
  
  return (
    <MainLayout>
      <div className="container py-8">
        <h1 className="text-3xl font-bold mb-6">{t('common.rentingGuides') || "租房攻略"}</h1>
        
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : guides.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {guides.map((guide) => (
              <Link to={`/guides/${guide.id}`} key={guide.id}>
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <div className="aspect-video relative overflow-hidden rounded-t-lg">
                    <img 
                      src={guide.cover_image_url || '/placeholder.svg'} 
                      alt={guide.title}
                      className="object-cover w-full h-full"
                    />
                  </div>
                  <CardHeader>
                    <h2 className="text-xl font-semibold">{guide.title}</h2>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground line-clamp-3">{guide.summary}</p>
                  </CardContent>
                  <CardFooter>
                    <div className="flex flex-wrap gap-2">
                      {guide.tags && guide.tags.map((tag, index) => (
                        <Badge key={index} variant="outline">{tag}</Badge>
                      ))}
                    </div>
                  </CardFooter>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-xl text-muted-foreground">{t('noGuides') || "目前尚無租房攻略"}</p>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default Guides;
